import { createTracedAsyncThunk } from '../../../tracing/trace';
import { Api } from '../../Api/Api';
import { AppDispatch, RootState } from '../Store';
import { AnalysisPersona, AnalysisSubject, FeedbackRating, IGrowerAnalysisResponse, ISalesRepAnalysisResponse, ISellerAnalysisResponse, ITerritoryAnalysisResponse } from './InsightsModels';

// Returns a string representing the AI experience level of the current user
// Unknown, None, MultiSalesRep, SalesRep, Seller, Grower
export const getAiExperience = createTracedAsyncThunk<string, null, { dispatch: AppDispatch, state: RootState }>(
	'get/aiexperience',
	async (context, request: null, thunkAPI) =>
	{
		try
		{
			const api = new Api('/api/7', thunkAPI.getState().auth.userAuthToken, context);
			const response = await api.getAsync<string>('ai/experience');

			if (response.ErrorCode === null && response.Success)
			{
				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

// Territories the current user is responsible for.
// A manager may sit on more than one hierarchy node, so this is always a list.
export const getTerritoryAnalyses = createTracedAsyncThunk<ITerritoryAnalysisResponse[], null, { dispatch: AppDispatch, state: RootState }>(
	'get/territoriesmulti/analyses',
	async (context, request: null, thunkAPI) =>
	{
		try
		{
			const api = new Api('/api/7', thunkAPI.getState().auth.userAuthToken, context);
			const response = await api.getAsync<ITerritoryAnalysisResponse[]>('ai/territories/analysis');

			if (response.ErrorCode === null && response.Success)
			{
				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

// Single Territory Analysis.
// The id is the hierarchy node's ExternalId, which can contain characters that need escaping.
export const getTerritoryAnalysis = createTracedAsyncThunk<ITerritoryAnalysisResponse, { territoryId: string, snapshotDate?: string }, { dispatch: AppDispatch, state: RootState }>(
	'get/territorysingle/analysis',
	async (context, request: { territoryId: string, snapshotDate?: string }, thunkAPI) =>
	{
		try
		{
			const api = new Api('/api/7', thunkAPI.getState().auth.userAuthToken, context);
			const { territoryId, snapshotDate } = request;
			let url = `ai/territories/node/${encodeURIComponent(territoryId)}`;
			if (snapshotDate)
			{
				url += `?snapshot=${snapshotDate}`;
			}
			const response = await api.getAsync<ITerritoryAnalysisResponse>(url);

			if (response.ErrorCode === null && response.Success)
			{
				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

// Multi Sales Rep Analyses
export const getSalesRepAnalyses = createTracedAsyncThunk<ISalesRepAnalysisResponse[], null, { dispatch: AppDispatch, state: RootState }>(
	'get/salesrepsmulti/analyses',
	async (context, request: null, thunkAPI) =>
	{
		try
		{
			const api = new Api('/api/7', thunkAPI.getState().auth.userAuthToken, context);
			const response = await api.getAsync<ISalesRepAnalysisResponse[]>('ai/salesreps/analysis');

			if (response.ErrorCode === null && response.Success)
			{
				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);


// Single Sales Rep Analysis
export const getSalesRepAnalysis = createTracedAsyncThunk<ISalesRepAnalysisResponse, { salesRepId: string, snapshotDate?: string }, { dispatch: AppDispatch, state: RootState }>(
	'get/salesrepsingle/analysis',
	async (context, request: { salesRepId: string, snapshotDate?: string }, thunkAPI) =>
	{
		try
		{
			const api = new Api('/api/7', thunkAPI.getState().auth.userAuthToken, context);
			const { salesRepId, snapshotDate } = request;
			let url = `ai/salesreps/${salesRepId}/analysis`;
			if (snapshotDate)
			{
				url += `?snapshot=${snapshotDate}`;
			}
			const response = await api.getAsync<ISalesRepAnalysisResponse>(url);

			if (response.ErrorCode === null && response.Success)
			{
				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

// Analysis for Seller members
export const getSellerMemberAnalysis = createTracedAsyncThunk<ISellerAnalysisResponse[], null, { dispatch: AppDispatch, state: RootState }>(
	'get/sellers/analysis',
	async (context, _, thunkAPI) =>
	{
		try
		{
			const api = new Api('/api/7', thunkAPI.getState().auth.userAuthToken, context);
			const url = 'ai/sellers/analysis';
			const response = await api.getAsync<ISellerAnalysisResponse[]>(url);

			if (response.ErrorCode === null && response.Success)
			{
				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

// Single Seller Analysis
export const getSellerAnalysis = createTracedAsyncThunk<ISellerAnalysisResponse, { sellerId: string, snapshotDate?: string }, { dispatch: AppDispatch, state: RootState }>(
	'get/sellersingle/analysis',
	async (context, request: { sellerId: string, snapshotDate?: string }, thunkAPI) =>
	{
		try
		{
			const api = new Api('/api/7', thunkAPI.getState().auth.userAuthToken, context);
			const { sellerId, snapshotDate } = request;
			let url = `ai/sellers/${sellerId}/analysis`;
			if (snapshotDate)
			{
				url += `?snapshot=${snapshotDate}`;
			}
			const response = await api.getAsync<ISellerAnalysisResponse>(url);

			if (response.ErrorCode === null && response.Success)
			{
				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

// Single Grower Analysis
export const getGrowerAnalysis = createTracedAsyncThunk<IGrowerAnalysisResponse, { growerId: string, snapshotDate?: string }, { dispatch: AppDispatch, state: RootState }>(
	'get/growersingle/analysis',
	async (context, request: { growerId: string, snapshotDate?: string }, thunkAPI) =>
	{
		try
		{
			const api = new Api('/api/7', thunkAPI.getState().auth.userAuthToken, context);
			const { growerId, snapshotDate } = request;
			let url = `ai/growers/${growerId}/analysis`;
			if (snapshotDate)
			{
				url += `?snapshot=${snapshotDate}`;
			}
			const response = await api.getAsync<IGrowerAnalysisResponse>(url);

			if (response.ErrorCode === null && response.Success)
			{
				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

export interface IConcernFeedbackRequest
{
	Persona: AnalysisPersona;
	Subject: AnalysisSubject;
	EntityId: string;
	SnapshotDate: string;
	ConcernId: string;
	Rating: FeedbackRating;
	Comments: string;
}

export interface IConcernFeedbackResponse
{
	Id: string;
	AiAnalysisRecord: string;
	AiAnalysisConcerndId: string;
	UserId: string;
	Comments: string;
	Created: string;
	Rating: FeedbackRating;
}

// Send Feedback - concern
export const sendFeedbackForConcern = createTracedAsyncThunk<IConcernFeedbackResponse, IConcernFeedbackRequest, { dispatch: AppDispatch, state: RootState }>(
	'post/aifeedback/concern',
	async (context, request, thunkAPI) =>
	{
		try
		{
			const api = new Api('/api/7', thunkAPI.getState().auth.userAuthToken, context);
			const { Persona, Subject, EntityId, SnapshotDate, ConcernId,  Rating, Comments  } = request;
			let url = `ai/personas/${Persona}/subjects/${Subject}/${EntityId}/snapshot/${SnapshotDate}`;
			if (ConcernId)
			{
				url += `/concerns/${ConcernId}/feedback`;
			}
			else
			{
				url += '/feedback';
			}
			const response = await api.postAsync<IConcernFeedbackResponse>(url, { Rating: Rating, Comments: Comments });

			if (response.ErrorCode === null && response.Success)
			{
				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);
